import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus, Upload } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, ProvedorCartoesRecolhidos, Selo } from '../../componentes/base';
import { Vazio } from '../../componentes/estados';
import { Formulario, Selecao, type DefCampo } from '../../componentes/formulario';
import { BotaoModeloCsv, DialogoImportacao } from '../../componentes/importacao';
import { BotaoLixeira } from '../../componentes/lixeira';
import { campoServico } from '../../componentes/cadastro-rapido';
import { Chatter } from '../../componentes/chatter';
import { CartaoVerticais, CampoVerticais, useVerticaisDaCriacao, verticaisDaCriacao } from '../../componentes/verticais';
import { Confirmar, Drawer } from '../../componentes/sobreposicao';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { useToast } from '../../componentes/toast';
import { del, getTodos, mensagemDeErro, patch, post } from '../../nucleo/api';
import { dinheiro, nomePessoa, textoLivre } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { USOS_SERVICO, type ServicoCatalogo } from './tipos-catalogos';
import { useMoedaDoFormulario, useMoedaSugerida } from '../../componentes/verticais';
import { exemploDinheiroEm } from '../../nucleo/mascaras';
import { opcoesMoeda } from '../../nucleo/paises';
import { simboloMoeda } from '../../nucleo/formato';

const CAMINHO = '/cadastros/servicos';
const COR_USO = { venda: 'verde', compra: 'azul', ambos: 'roxo' } as const;

/** Fase 2 (W18, K2): profissional ou recurso da Agenda (lido de `/agenda/recursos`, só para quem vê a Agenda). */
interface RecursoAgenda {
  id: string;
  nome: string;
  tipo: string;
  ativo: boolean;
}
/** Nome do profissional pelo `nomePessoa` (modo discreto); sala e equipamento pelo `textoLivre`. */
const nomeRecurso = (r: RecursoAgenda) => (r.tipo === 'profissional' ? nomePessoa(r.nome) : textoLivre(r.nome));

/**
 * Fase 2 (W6, L12): catálogo único de Serviços (venda e compra). Centro de custo e categoria padrão vêm do Financeiro
 * (lista dele, só para quem o lê); são sugestão para quem usar o serviço.
 */
export function Servicos() {
  const { t } = useTranslation('cadastros');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [aberto, setAberto] = useState<ServicoCatalogo | 'novo' | null>(null);
  const [importar, setImportar] = useState(false);
  const [excluindo, setExcluindo] = useState(false);
  const [uso, setUso] = useState('');
  const atual = aberto && aberto !== 'novo' ? aberto : null;
  // Ficha 2026-10-22: as verticais já na criação (null = o escopo de quem cria, como antes).
  const [verticais, setVerticais] = useVerticaisDaCriacao(aberto);
  // Lista de 04/11, item 3 (D2-A): a moeda inicial é a da vertical escolhida; sem ela, a do workspace. Editando, a do serviço.
  const sugerida = useMoedaSugerida(verticais);
  const moedaForm = useMoedaDoFormulario(sugerida);
  const [moedaVista, setMoedaVista] = useState(sugerida);
  const podeEditar = atual ? s.pode('cadastros.editar') : s.pode('cadastros.criar');
  const leFinanceiro = s.instalado('financeiro') && s.pode('financeiro.ver');
  // Fase 2 (W18, K2): a Agenda agenda por este catálogo; duração e quem faz aparecem para o serviço agendável.
  const usaAgenda = s.instalado('agenda') && s.pode('agenda.ver');
  const [agendavel, setAgendavel] = useState(false);
  const [permitidos, setPermitidos] = useState<string[]>([]);
  const recursos = useQuery({
    queryKey: [CAMINHO, 'recursos-agenda'],
    queryFn: () => getTodos<RecursoAgenda>('/agenda/recursos'),
    enabled: usaAgenda && !!aberto,
    staleTime: 30_000,
  });
  // Ativos mais os já escolhidos (o recurso arquivado depois segue marcado até a pessoa desmarcar).
  const opcoesRecurso = (recursos.data?.itens ?? []).filter((r) => r.ativo || permitidos.includes(r.id));
  const abrir = (x: ServicoCatalogo | 'novo') => {
    moedaForm.reiniciar();
    setAgendavel(x !== 'novo' && x.agendavel);
    setPermitidos(x === 'novo' ? [] : (x.recurso_ids ?? []));
    setAberto(x);
  };

  const excluir = useMutation({
    mutationFn: () => del(`${CAMINHO}/${atual!.id}`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: [CAMINHO] });
      setExcluindo(false);
      setAberto(null);
      toast.sucesso(tn('comum.removido'));
    },
    onError: (e) => (setExcluindo(false), toast.erro(mensagemDeErro(e))),
  });

  const campos: DefCampo[] = [
    // Fase 2 (W25, K14): os campos essenciais vêm do núcleo (`campoServico`), os mesmos do cadastro rápido no item da compra.
    campoServico(t, 'nome', !podeEditar),
    campoServico(t, 'codigo', !podeEditar),
    campoServico(t, 'uso', !podeEditar),
    campoServico(t, 'tipo_cobranca', !podeEditar),
    { nome: 'agendavel', rotulo: t('servicos.campos.agendavel'), tipo: 'booleano', largura: 'terco', ajuda: t('servicos.ajuda.agendavel'), desabilitado: !podeEditar },
    ...(usaAgenda
      ? ([
          {
            nome: 'duracao_min',
            rotulo: t('servicos.campos.duracao'),
            tipo: 'numero',
            largura: 'terco',
            min: 5,
            max: 1440,
            passo: 5,
            ajuda: t('servicos.ajuda.duracao'),
            visivel: (v) => v.agendavel === true,
            desabilitado: !podeEditar,
          },
        ] satisfies DefCampo[])
      : []),
    { ...campoServico(t, 'preco_venda_centavos', !podeEditar), rotulo: `${t('servicos.campos.preco')} (${simboloMoeda(moedaVista)})`, placeholder: exemploDinheiroEm(simboloMoeda(moedaVista)) },
    { ...campoServico(t, 'custo_centavos', !podeEditar), rotulo: `${t('servicos.campos.custo')} (${simboloMoeda(moedaVista)})`, placeholder: exemploDinheiroEm(simboloMoeda(moedaVista)) },
    { nome: 'moeda', rotulo: t('servicos.campos.moeda'), tipo: 'selecao', obrigatorio: true, largura: 'terco', desabilitado: !podeEditar, opcoes: opcoesMoeda(atual?.moeda ?? moedaVista) },
    { nome: 'ativo', rotulo: t('servicos.campos.ativo'), tipo: 'booleano', largura: 'terco', desabilitado: !podeEditar },
    ...(leFinanceiro
      ? ([
          {
            nome: 'centro_custo_padrao_id',
            rotulo: t('servicos.campos.centro'),
            tipo: 'busca',
            largura: 'meia',
            ajuda: t('servicos.ajuda.padrao'),
            busca: { caminho: '/financeiro/centros-custo', params: { ativo: true }, rotulo: (i) => [i.codigo, i.nome].filter(Boolean).join(' ') },
            desabilitado: !podeEditar,
          },
          {
            nome: 'categoria_padrao_id',
            rotulo: t('servicos.campos.categoria'),
            tipo: 'busca',
            largura: 'meia',
            ajuda: t('servicos.ajuda.padrao'),
            busca: { caminho: '/financeiro/categorias', params: { ativa: true, lancavel: true }, rotulo: (i) => [i.codigo, i.nome].filter(Boolean).join(' ') },
            desabilitado: !podeEditar,
          },
        ] satisfies DefCampo[])
      : []),
    { nome: 'descricao', rotulo: t('servicos.campos.descricao'), tipo: 'area', desabilitado: !podeEditar },
  ];

  const colunas: Coluna<ServicoCatalogo>[] = [
    { chave: 'nome', rotulo: t('servicos.campos.nome'), principal: true, ordenavel: true, render: (x) => <span className="font-medium">{textoLivre(x.nome)}</span> },
    { chave: 'codigo', rotulo: t('servicos.campos.codigo'), ordenavel: true, mono: true, render: (x) => x.codigo ?? '' },
    { chave: 'uso', rotulo: t('servicos.campos.uso'), render: (x) => <Selo cor={COR_USO[x.uso]}>{t(`servicos.usos.${x.uso}`)}</Selo> },
    { chave: 'tipo_cobranca', rotulo: t('servicos.campos.tipoCobranca'), render: (x) => t(`servicos.cobrancas.${x.tipo_cobranca}`) },
    { chave: 'preco_venda_centavos', rotulo: t('servicos.campos.preco'), ordenavel: true, alinhar: 'direita', mono: true, render: (x) => dinheiro(x.preco_venda_centavos, x.moeda) },
    { chave: 'custo_centavos', rotulo: t('servicos.campos.custo'), alinhar: 'direita', mono: true, oculta: true, render: (x) => dinheiro(x.custo_centavos, x.moeda) },
    { chave: 'agendavel', rotulo: t('servicos.campos.agendavel'), oculta: true, render: (x) => (x.agendavel ? tn('comum.sim') : tn('comum.nao')) },
    { chave: 'duracao_min', rotulo: t('servicos.campos.duracao'), alinhar: 'direita', mono: true, oculta: true, render: (x) => (x.agendavel ? `${x.duracao_min ?? 60} min` : '') },
    { chave: 'ativo', rotulo: t('servicos.campos.ativo'), render: (x) => (x.ativo ? tn('comum.sim') : tn('comum.nao')) },
  ];

  return (
    <>
      {s.pode('cadastros.criar') && (
        <AcaoPrimaria>
          {s.pode('cadastros.excluir') && <BotaoLixeira entidades={['cadastros.servico']} />}
          {s.pode('cadastros.importar') && (
            <>
              <BotaoModeloCsv caminho={CAMINHO} />
              <Botao icone={<Upload className="size-4" />} onClick={() => setImportar(true)}>
                {t('acoes.importar')}
              </Botao>
            </>
          )}
          <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => abrir('novo')}>
            {t('servicos.novo')}
          </Botao>
        </AcaoPrimaria>
      )}
      <Tabela<ServicoCatalogo>
        tela="cadastros.servicos"
        caminho={CAMINHO}
        params={{ uso: uso || undefined }}
        filtros={{ uso }}
        aoAplicarFiltros={(f) => setUso(String(f.uso ?? ''))}
        colunas={colunas}
        entidade="cadastros.servico"
        aoClicar={abrir}
        vazio={<Vazio texto={t('servicos.vazio')} />}
        filtrosExtra={
          <div className="w-full sm:w-56">
            <Selecao aria-label={t('servicos.filtros.uso')} opcoes={USOS_SERVICO.map((u) => ({ valor: u, rotulo: t(`servicos.usos.${u}`) }))} vazio={t('servicos.filtros.todos')} value={uso} onChange={(e) => setUso(e.target.value)} />
          </div>
        }
      />
      <Drawer
        aberto={!!aberto}
        aoFechar={() => setAberto(null)}
        largura="lg"
        titulo={atual ? textoLivre(atual.nome) : t('servicos.novo')}
        rodape={
          <>
            {atual && s.pode('cadastros.excluir') && (
              <Botao variante="perigo" className="mr-auto" onClick={() => setExcluindo(true)}>
                {t('servicos.excluir')}
              </Botao>
            )}
            <Botao variante="fantasma" onClick={() => setAberto(null)}>
              {tn('comum.cancelar')}
            </Botao>
            {podeEditar && (
              <Botao variante="primario" type="submit" form="form-servico-catalogo">
                {tn('comum.salvar')}
              </Botao>
            )}
          </>
        }
      >
        <Formulario
          key={atual?.id ?? 'novo'}
          id="form-servico-catalogo"
          campos={campos}
          entidade="cadastros.servico"
          inicial={(atual ?? { uso: 'venda', tipo_cobranca: 'avulso', agendavel: false, ativo: true, preco_venda_centavos: 0, custo_centavos: 0, moeda: sugerida }) as unknown as Record<string, unknown>}
          acompanhar={atual ? undefined : moedaForm.acompanhar}
          aoMudar={(v) => {
            setAgendavel(v.agendavel === true);
            setMoedaVista(typeof v.moeda === 'string' && v.moeda ? v.moeda : sugerida);
            if (!atual) moedaForm.observar(v);
          }}
          aoEnviar={async (v) => {
            const corpo: Record<string, unknown> = {
              nome: v.nome,
              codigo: (v.codigo as string) || null,
              descricao: (v.descricao as string) || null,
              uso: v.uso,
              tipo_cobranca: v.tipo_cobranca,
              agendavel: v.agendavel === true,
              ativo: v.ativo !== false,
              preco_venda_centavos: (v.preco_venda_centavos as number | null) ?? 0,
              custo_centavos: (v.custo_centavos as number | null) ?? 0,
              moeda: v.moeda,
              extras: v.extras ?? {},
            };
            // Sem acesso ao Financeiro os padrões nem aparecem: o que já estava gravado não é tocado.
            if (leFinanceiro) {
              corpo.centro_custo_padrao_id = v.centro_custo_padrao_id || null;
              corpo.categoria_padrao_id = v.categoria_padrao_id || null;
            }
            // Duração e quem faz só para o agendável e para quem vê a Agenda; fora disso, o que estava gravado fica.
            if (usaAgenda && v.agendavel === true) {
              corpo.duracao_min = (v.duracao_min as number | null) ?? null;
              corpo.recurso_ids = permitidos;
            }
            if (atual) await patch(`${CAMINHO}/${atual.id}`, corpo);
            else await post(CAMINHO, { ...corpo, ...verticaisDaCriacao(verticais) });
            await qc.invalidateQueries({ queryKey: [CAMINHO] });
            toast.sucesso(tn('comum.salvo'));
            setAberto(null);
          }}
        >
          {!atual && <CampoVerticais valor={verticais} aoMudar={setVerticais} />}
          {usaAgenda && agendavel && (
            <fieldset className="sm:col-span-6" data-quem-faz="">
              <legend className="mb-1 text-sm font-medium text-tinta-2">{t('servicos.campos.quemFaz')}</legend>
              <p className="mb-2 text-xs text-tinta-3">{t('servicos.ajuda.quemFaz')}</p>
              <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                {opcoesRecurso.map((r) => (
                  <label key={r.id} className="flex cursor-pointer items-center gap-2 rounded-lg border border-borda px-3 py-2 text-sm hover:bg-sup-2">
                    <input
                      type="checkbox"
                      disabled={!podeEditar}
                      checked={permitidos.includes(r.id)}
                      onChange={(e) => setPermitidos((l) => (e.target.checked ? [...l, r.id] : l.filter((x) => x !== r.id)))}
                    />
                    <span className="truncate" title={nomeRecurso(r)}>
                      {nomeRecurso(r)}
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
          )}
        </Formulario>
        {atual && (
          <div className="mt-6 border-t border-borda pt-4">
            {/* Item 10 (21/10): cartão recolhido, como na ficha de Pessoas e empresas. */}
            <ProvedorCartoesRecolhidos>
              <CartaoVerticais entidade="cadastros.servico" registroId={atual.id} podeEditar={s.pode('cadastros.editar')} />
            </ProvedorCartoesRecolhidos>
            <Chatter entidade="cadastros.servico" moeda={atual.moeda} registroId={atual.id} titulo={atual.nome} />
          </div>
        )}
      </Drawer>
      <Confirmar
        aberto={excluindo}
        aoFechar={() => setExcluindo(false)}
        aoConfirmar={() => excluir.mutate()}
        carregando={excluir.isPending}
        perigoso
        titulo={t('servicos.excluirTitulo')}
        texto={t('servicos.excluirTexto')}
      />
      <DialogoImportacao aberto={importar} aoFechar={() => setImportar(false)} caminho={CAMINHO} titulo={t('servicos.importar')} />
    </>
  );
}
